import type {OrderLine} from '../../transport/apiServiceResponse';
import {getProductDisplayRules} from '../../utils/rewards';

type Compare = (a: OrderLine, b: OrderLine) => number;

const ascending =
  (key: (line: OrderLine) => number): Compare =>
  (a, b) =>
    key(a) - key(b);
const descending =
  (key: (line: OrderLine) => number): Compare =>
  (a, b) =>
    key(b) - key(a);

// Measured 2026-11-04 against the sorted specials of one Bottlemart, Harry Brown and Thirsty Camel
// shop, as a guest, 1,183 lines: each key below has 0 adjacent pairs out of order.
const shopperPrice = (line: OrderLine) => line.sell_price_cents;
const discountCents = (line: OrderLine) => line.normal_price_cents - line.sell_price_cents;
const discountWholePercent = (line: OrderLine) =>
  line.normal_price_cents > 0
    ? Math.floor((100 * discountCents(line)) / line.normal_price_cents)
    : 0;

// Lowercase, then UTF-16 code unit order. Measured 2026-11-04 on the name-sorted lines of 4 shops,
// one per brand, as a guest: 17 of 7,966 adjacent pairs are out of order, against 25 for
// `localeCompare(b, undefined, {sensitivity: 'base'})`. It also does not vary by device locale.
const compareNames: Compare = (a, b) => {
  const nameA = a.name.toLowerCase();
  const nameB = b.name.toLowerCase();
  if (nameA === nameB) return 0;
  return nameA < nameB ? -1 : 1;
};

const CHOSEN_SORTS: Record<string, Compare> = {
  name: compareNames,
  name_descending: (a, b) => compareNames(b, a),
  price: ascending(shopperPrice),
  price_descending: descending(shopperPrice),
  // The payload has no unit price. `unit_price` measured as sell price order: see above.
  unit_price: ascending(shopperPrice),
  discount_percent: descending(discountWholePercent),
  discount_cents: descending(discountCents),
};

type Args = {
  specials: OrderLine[];
  publicSet?: OrderLine[];
  privateSet?: OrderLine[];
  /** The sort the server applied to `specials`. */
  sortOptionId: string;
  specialsHasNextPage: boolean;
};

/**
 * DIS-2053: the specials list with the member_price sets merged in, deduped by line id.
 * A member line that is also a special takes its member position.
 *
 * Without a chosen sort: public lines, then private lines, then specials lines.
 * With a chosen sort: a walk over the specials in server order. Before each specials line it
 * places the member lines at or before that line. The remainder waits for the last specials page,
 * because a later page can still hold lines that sort before it. The walk reads only the loaded
 * prefix, so a loaded page appends to the result and never moves a line already in it.
 */
export function mergeSpecialsLines({
  specials,
  publicSet = [],
  privateSet = [],
  sortOptionId,
  specialsHasNextPage,
}: Args): OrderLine[] {
  const seen = new Set<string>();
  const members: OrderLine[] = [];
  const addMember = (line: OrderLine) => {
    if (seen.has(line.id)) return;
    seen.add(line.id);
    members.push(line);
  };
  publicSet.filter(l => getProductDisplayRules(l).presentAsPublicMemberProduct).forEach(addMember);
  privateSet
    .filter(l => getProductDisplayRules(l).presentAsPrivateMemberProduct)
    .forEach(addMember);

  const isNewSpecial = (line: OrderLine) => {
    if (seen.has(line.id)) return false;
    seen.add(line.id);
    return true;
  };

  const compare = CHOSEN_SORTS[sortOptionId];
  if (!compare) return [...members, ...specials.filter(isNewSpecial)];

  const queue = [...members].sort(compare);
  const merged: OrderLine[] = [];
  let next = 0;
  for (const line of specials) {
    while (next < queue.length && compare(queue[next], line) <= 0) merged.push(queue[next++]);
    if (isNewSpecial(line)) merged.push(line);
  }
  return specialsHasNextPage ? merged : [...merged, ...queue.slice(next)];
}
